import { api } from ".";

export type DraftRunStatus =
  | "collecting"
  | "queued"
  | "running"
  | "worker_returned"
  | "awaiting_input"
  | "completed"
  | "failed"
  | "dead"
  | "canceled";

export type DraftConfidence = "High" | "Medium" | "Low";

export type DraftRecStatus =
  | "pending"
  | "accepted"
  | "overridden"
  | "rejected"
  | "created"
  | "needs_input"
  | "superseded";

export type DraftComponentType =
  | "provider_decomposition"
  | "payer_consolidation"
  | "contract_term"
  | "base_rate_file"
  | "rate_calculation"
  | "carveout"
  | "case_rate_ruleset"
  | "inpatient_ruleset"
  | "package_ruleset"
  | "exploding_fee_schedule"
  | "mpdr_ruleset"
  | "lesser_of_charges"
  | "charge_based_pricing"
  | "unit_cap_ruleset"
  | "ma_payer_link";

export type DraftOverrideCategory =
  | "agent_misread_source"
  | "agent_missed_document"
  | "wrong_policy_mapping"
  | "contract_ambiguous";

export type DraftDocKind =
  | "contract_pdf"
  | "rate_excel"
  | "amendment"
  | "carveout_exhibit"
  | "other";

export interface IDraftAttribution {
  id: string;
  documentId: string | null;
  locator: string | null;
  fileName: string | null;
  exactQuote: string;
  verified: boolean;
  verificationMethod: string | null;
  transform: string | null;
}

export interface IDraftOverride {
  category: DraftOverrideCategory;
  reason: string;
  finalValue: unknown | null;
  agentWasWrong: boolean;
  createdAt: string;
}

export interface IValidationError {
  path: string;
  message: string;
}

/** Content verification of a base_rate_file recommendation against the live file (see backend). */
export interface IRateTableDiff {
  recCount: number;
  prodCount: number;
  agree: number;
  differ: { cptCode: string; recRate: number; prodRate: number }[];
  differCount: number;
  onlyInRec: string[];
  onlyInRecCount: number;
  onlyInProdCount: number;
  verdict: "match" | "differs";
}

export type IContentVerifyResult =
  | {
      status: "match" | "differs";
      comparedToFile: string;
      diff: IRateTableDiff;
    }
  | { status: "no_production_file" }
  | { status: "not_generatable"; message: string };

export type DraftChangeKind =
  | "new"
  | "unchanged"
  | "modified"
  | "conflicted"
  | "superseded";

/** A field the reviewer CORRECTED that the agent has since moved to a third value. She decides. */
export interface IFieldConflict {
  path: string;
  /** What the agent proposed last version. */
  base: unknown;
  /** What she corrected it to. We hold this until she resolves the conflict. */
  ours: unknown;
  /** What the agent proposes now. */
  theirs: unknown;
}

export interface IMergeDetails {
  conflicts: IFieldConflict[];
  changedFields: string[];
}

export interface IDraftMessage {
  id: string;
  stepId: string | null;
  recommendationId: string | null;
  role: string;
  body: string;
  /** Null = the agent has not seen this yet. */
  appliedInVersionId: string | null;
  createdAt: string;
}

export interface IMergeSummary {
  total: number;
  unchanged: number;
  modified: number;
  conflicted: number;
  new: number;
  superseded: number;
  /** She already BUILT these and they changed or were withdrawn. The loudest state in the tool. */
  alreadyBuilt: number;
}

export interface IDraftRecommendation {
  id: string;
  componentType: DraftComponentType;
  proposedModeling: unknown;
  /** The agent's original proposal, captured the first time a human edits. Null = untouched. */
  originalModeling: unknown | null;
  /** Field-scoped schema failures. Non-empty ⇒ status is `needs_input` and the rec must be fixed. */
  validationErrors: IValidationError[] | null;
  /** How this relates to the previous version. Null on a run that has only ever been generated once. */
  changeKind: DraftChangeKind | null;
  mergeDetails: IMergeDetails | null;
  /** The agent's account of WHY this moved. Cause attribution — a change she can't explain, she can't trust. */
  changeReason: string | null;
  stableKey: string | null;
  altModeling: unknown | null;
  confidence: DraftConfidence;
  status: DraftRecStatus;
  missingInputs: unknown | null;
  openQuestions: unknown | null;
  implementedAt: string | null;
  sortOrder: number;
  attributions: IDraftAttribution[];
  override: IDraftOverride | null;
}

export interface IDraftDocument {
  id: string;
  kind: DraftDocKind;
  fileName: string;
  contentType: string | null;
  fileSize: number | null;
  extractionStatus: "pending" | "extracting" | "ready" | "failed";
}

export interface IDraftRunSummary {
  id: string;
  accountId: string;
  name: string | null;
  status: DraftRunStatus;
  createdById: string;
  providerId: string | null;
  providerName: string | null;
  payerId: number | null;
  payerName: string | null;
  siteTier: DraftSiteTier | null;
  /** Free-text steering the reviewer gave the model at generation time (e.g. "model Network P only"). */
  generationInstructions: string | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
  recommendationCount: number;
}

export interface IDraftRunDetail extends IDraftRunSummary {
  latestVersionId: string | null;
  versionNo: number;
  documents: IDraftDocument[];
  recommendations: IDraftRecommendation[];
  messages: IDraftMessage[];
  /** There is input the agent has not seen: a queued instruction, or a document added since the last run. */
  isStale: boolean;
  pendingInstructionCount: number;
  /** The last regeneration's diff. `unchanged / total` is the trust metric — shown, not hidden. */
  mergeSummary: IMergeSummary | null;
}

export interface IPresignedUpload {
  documentId: string;
  uploadUrl: string;
  s3Key: string;
}

export type DraftCompareStatus = "match" | "missing" | "differs" | "extra";

export interface IDraftCompareItem {
  recommendationId: string | null;
  componentType: string | null;
  liveKey: string | null;
  status: DraftCompareStatus;
}

export interface IDraftCompareResult {
  compareId: string;
  items: IDraftCompareItem[];
}

// Per-recommendation self-verify result: how one recommendation lines up with production.
export type DraftVerdict = "match" | "differs" | "present" | "missing" | "n_a";
export interface IDraftRecMatch {
  verdict: DraftVerdict;
  rows: Record<string, unknown>[];
}
// Keyed by recommendationId.
export type IDraftVerification = Record<string, IDraftRecMatch>;

/** "This contract looks modeled under a different provider" — the sibling entities that already have it. */
export interface IProviderHint {
  otherProviders: { id: string; name: string }[];
}

export type DraftSiteTier = "non_facility" | "facility" | "both";

export interface ICreateDraftRunRequest {
  name?: string;
  providerId?: string;
  payerId?: number;
  siteTier?: DraftSiteTier;
  note?: string;
  idempotencyKey?: string;
}

export interface IPresignDocumentRequest {
  runId: string;
  fileName: string;
  contentType: string;
  kind?: DraftDocKind;
  fileSize?: number;
}

export interface IOverrideRequest {
  recommendationId: string;
  category: DraftOverrideCategory;
  reason: string;
  finalValue?: unknown;
  agentWasWrong?: boolean;
}

export interface IAcceptRequest {
  recommendationId: string;
  editedModeling?: Record<string, unknown>;
}

const contractModelDraftsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    listContractModelDraftRuns: builder.query<IDraftRunSummary[], void>({
      query: () => ({ url: "/contract-model-drafts/runs" }),
      transformResponse: (r: { data: IDraftRunSummary[] }) => r.data,
      providesTags: ["ContractModelDrafts"],
    }),
    getContractModelDraftRun: builder.query<IDraftRunDetail, string>({
      query: (id) => ({ url: `/contract-model-drafts/runs/${id}` }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      providesTags: ["ContractModelDrafts"],
    }),
    createContractModelDraftRun: builder.mutation<
      IDraftRunDetail,
      ICreateDraftRunRequest
    >({
      query: (body) => ({
        url: "/contract-model-drafts/runs",
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    presignContractModelDraftDocument: builder.mutation<
      IPresignedUpload,
      IPresignDocumentRequest
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IPresignedUpload }) => r.data,
      // Deliberately does NOT invalidate: this only reserves a row; the bytes aren't in S3 yet.
      // confirmContractModelDraftDocument invalidates once the upload is verified, so the
      // document list never shows a file that isn't really there.
    }),
    confirmContractModelDraftDocument: builder.mutation<
      IDraftRunDetail,
      { runId: string; documentId: string }
    >({
      query: ({ runId, documentId }) => ({
        url: `/contract-model-drafts/runs/${runId}/documents/${documentId}/confirm`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    runContractModelDraftGeneration: builder.mutation<
      IDraftRunDetail,
      { runId: string; instructions?: string }
    >({
      query: ({ runId, instructions }) => ({
        url: `/contract-model-drafts/runs/${runId}/generate`,
        method: "POST",
        // Always send the field (even empty) so an emptied box clears the stored value server-side.
        body: { instructions: instructions ?? "" },
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    acceptContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IAcceptRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/accept`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    rejectContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/reject`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Queue an instruction for the next regeneration. Deliberately does NOT start one. */
    addContractModelDraftMessage: builder.mutation<
      IDraftRunDetail,
      {
        runId: string;
        body: string;
        stepId?: string;
        recommendationId?: string;
      }
    >({
      query: ({ runId, ...body }) => ({
        url: `/contract-model-drafts/runs/${runId}/messages`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    deleteContractModelDraftMessage: builder.mutation<
      IDraftRunDetail,
      { runId: string; messageId: string }
    >({
      query: ({ runId, messageId }) => ({
        url: `/contract-model-drafts/runs/${runId}/messages/${messageId}`,
        method: "DELETE",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Re-derive the whole model into a new version, then diff it against the last one. */
    regenerateContractModelDraftRun: builder.mutation<IDraftRunDetail, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/regenerate`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),

    /** Undo a reject — what makes deselecting a service line reversible. */
    restoreContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/restore`,
        method: "POST",
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    setContractModelDraftRecommendationImplemented: builder.mutation<
      IDraftRunDetail,
      { runId: string; recommendationId: string; implemented: boolean }
    >({
      query: ({ recommendationId, implemented }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/implemented`,
        method: "POST",
        body: { implemented },
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      // Ticking a checkbox is a one-field change, so flip it in the cache immediately (instant feedback)
      // and reconcile with the server's run when it returns — no full-run refetch, no invalidatesTags.
      onQueryStarted: async (
        { runId, recommendationId, implemented },
        { dispatch, queryFulfilled }
      ) => {
        const patch = dispatch(
          contractModelDraftsQueries.util.updateQueryData(
            "getContractModelDraftRun",
            runId,
            (draft) => {
              const rec = draft.recommendations.find(
                (r) => r.id === recommendationId
              );
              if (rec)
                rec.implementedAt = implemented
                  ? new Date().toISOString()
                  : null;
            }
          )
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            contractModelDraftsQueries.util.updateQueryData(
              "getContractModelDraftRun",
              runId,
              (draft) => Object.assign(draft, data)
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),
    runContractModelDraftCompare: builder.mutation<
      IDraftCompareResult,
      { runId: string }
    >({
      query: ({ runId }) => ({
        url: `/contract-model-drafts/runs/${runId}/compare`,
        method: "POST",
        body: {},
      }),
      transformResponse: (r: { data: IDraftCompareResult }) => r.data,
    }),
    getContractModelDraftVerification: builder.query<
      IDraftVerification,
      string
    >({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/verification`,
      }),
      transformResponse: (r: { data: IDraftVerification }) => r.data,
    }),
    // "Nothing is modeled for this run's provider+payer, but a sibling provider does have it" — surfaced
    // as a banner so a wrong-provider run doesn't read as an entirely un-modeled contract.
    getContractModelDraftProviderHint: builder.query<IProviderHint, string>({
      query: (runId) => ({
        url: `/contract-model-drafts/runs/${runId}/provider-hint`,
      }),
      transformResponse: (r: { data: IProviderHint }) => r.data,
    }),
    overrideContractModelDraftRecommendation: builder.mutation<
      IDraftRunDetail,
      IOverrideRequest
    >({
      query: ({ recommendationId, ...body }) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/override`,
        method: "POST",
        body,
      }),
      transformResponse: (r: { data: IDraftRunDetail }) => r.data,
      invalidatesTags: ["ContractModelDrafts"],
    }),
    // Generates the base-rate-file CSV a base_rate_file recommendation calls for (Medicare fee schedule)
    // and returns it as a blob so the caller can trigger a browser download.
    getContractModelDraftBaseRateFileCsv: builder.query<
      { blob: Blob; fileName: string },
      string
    >({
      query: (recommendationId) => ({
        url: `/contract-model-drafts/recommendations/${recommendationId}/base-rate-file.csv`,
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            try {
              return await response.json();
            } catch {
              return { message: "Could not generate the base rate file." };
            }
          }
          const cd = response.headers.get("content-disposition") ?? "";
          const match = cd.match(/filename="?([^"]+)"?/);
          return {
            blob: await response.blob(),
            fileName: match ? match[1] : "base-rate-file.csv",
          };
        },
      }),
    }),
    // Compares the CPT→rate table a base_rate_file recommendation WOULD generate against the base rate
    // file production actually prices this provider+payer against — content verification, not name match.
    getContractModelDraftBaseRateFileContentDiff: builder.query<
      { data: IContentVerifyResult; message: string },
      string
    >({
      query: (recommendationId) =>
        `/contract-model-drafts/recommendations/${recommendationId}/verify-contents`,
    }),
  }),
});

export const {
  useListContractModelDraftRunsQuery,
  useGetContractModelDraftRunQuery,
  useCreateContractModelDraftRunMutation,
  useConfirmContractModelDraftDocumentMutation,
  usePresignContractModelDraftDocumentMutation,
  useRunContractModelDraftGenerationMutation,
  useAcceptContractModelDraftRecommendationMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRestoreContractModelDraftRecommendationMutation,
  useAddContractModelDraftMessageMutation,
  useDeleteContractModelDraftMessageMutation,
  useRegenerateContractModelDraftRunMutation,
  useSetContractModelDraftRecommendationImplementedMutation,
  useRunContractModelDraftCompareMutation,
  useGetContractModelDraftVerificationQuery,
  useLazyGetContractModelDraftVerificationQuery,
  useGetContractModelDraftProviderHintQuery,
  useOverrideContractModelDraftRecommendationMutation,
  useLazyGetContractModelDraftBaseRateFileCsvQuery,
  useLazyGetContractModelDraftBaseRateFileContentDiffQuery,
} = contractModelDraftsQueries;
